// Shown once after a sign-up or after new recovery codes are made: the codes, plus every way to
// keep them (printable PDF that opens by itself, cloud folder, share sheet, ZIP) and an optional
// password for the PDF. The user must confirm they saved the codes before they leave the screen.
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Body, Button, Caption, Field, Section, ToggleRow } from './ui';
import { appOrigin, canSaveToFolder, canShare, downloadZip, makeKit, openPdf, saveToFolder, shareFiles, type Kit } from '../platform/recoveryKit';
import type { KitStrings } from '../logic/recoveryKit';
import { generatePassword, passwordProblem } from '../logic/passwords';
import { font, radius, spacing, usePalette } from '../theme';

const MONO = Platform.select({ web: '"IBM Plex Mono", ui-monospace, monospace', default: 'Courier' });

export default function RecoveryKitPanel({ codes, displayName, onDone }: { codes: string[]; displayName: string; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const p = usePalette();
  const [plainKit, setPlainKit] = useState<Kit | null>(null);
  const [lockedKit, setLockedKit] = useState<Kit | null>(null);
  const [protect, setProtect] = useState(false);
  const [password, setPassword] = useState('');
  const [building, setBuilding] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const autoOpened = useRef(false);

  const kitInput = (pw?: string) => ({
    codes, displayName, password: pw, signInUrl: `${appOrigin}/settings/account`, date: new Date(),
    strings: {
      brand: 'Chefi', title: t('account.kit.title'),
      account: t('account.kit.account', { name: displayName || 'Chefi', date: new Date().toLocaleDateString(i18n.language) }),
      intro: t('account.kit.intro'), codesHeading: t('account.kit.codes'), tickHint: t('account.kit.tick'), stepsTitle: t('account.kit.stepsTitle'),
      steps: [t('account.kit.step1'), t('account.kit.step2'), t('account.kit.step3')],
      keepTitle: t('account.kit.keepTitle'), keep: [t('account.kit.keep1'), t('account.kit.keep2'), t('account.kit.keep3')],
      qrCaption: t('account.kit.qr'), footer: t('account.kit.footer'), readme: t('account.kit.readme'),
      encryptedNote: t('account.kit.encryptedNote'), readmeEncrypted: t('account.kit.readmeEncrypted'),
    } satisfies KitStrings,
  });

  useEffect(() => {
    let alive = true;
    makeKit(kitInput())
      .then(k => {
        if (!alive) return;
        setPlainKit(k);
        // The PDF opens by itself; if the browser blocks the window the button below still works.
        if (!autoOpened.current) {
          autoOpened.current = true;
          if (!openPdf(k)) setStatus(t('account.save.blocked'));
        }
      })
      .catch(() => alive && setStatus(t('account.errors.generic')));
    return () => {
      alive = false;
    };
    // The codes are fixed for the life of this panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const problem = protect && password.length > 0 ? passwordProblem(password) : null;
  // The kit every button below acts on. With protection on, the plain one is never used by mistake.
  const kit = protect ? lockedKit : plainKit;

  const buildLocked = async () => {
    if (passwordProblem(password)) return;
    setBuilding(true);
    setStatus(null);
    try {
      setLockedKit(await makeKit(kitInput(password)));
      setStatus(t('account.protect.ready'));
    } catch {
      setStatus(t('account.errors.generic'));
    } finally {
      setBuilding(false);
    }
  };

  const attempt = async (fn: () => Promise<string | null>) => {
    try {
      setStatus(await fn());
    } catch {
      setStatus(t('account.errors.generic'));
    }
  };

  return (
    <Section title={t('account.save.title')} icon="lock">
      <Body secondary>{t('account.save.shownOnce')}</Body>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.s }}>
        {codes.map(c => (
          <View key={c} style={{ backgroundColor: p.card, borderRadius: radius.m, paddingVertical: spacing.s, paddingHorizontal: spacing.m, flexBasis: '45%', flexGrow: 1 }}>
            <Text selectable style={{ color: p.text, fontFamily: MONO, fontSize: font.body, fontWeight: '600', letterSpacing: 1 }}>{c}</Text>
          </View>
        ))}
      </View>

      <ToggleRow label={t('account.protect.toggle')} note={t('account.protect.hint')} value={protect} onChange={v => { setProtect(v); setStatus(null); }} />
      {protect ? (
        <>
          <Field
            label={t('account.protect.password')} value={password} autoCapitalize="none" autoCorrect={false} spellCheck={false}
            onChangeText={v => { setPassword(v); setLockedKit(null); }}
          />
          {problem ? <Caption color={p.danger}>{t('account.protect.tooShort')}</Caption> : null}
          <Button label={t('account.protect.generate')} icon="sparkles" variant="secondary" onPress={() => { setPassword(generatePassword()); setLockedKit(null); setStatus(t('account.protect.writeDown')); }} />
          <Button label={building ? t('account.protect.working') : t('account.protect.make')} icon="lock" loading={building} disabled={building || password.length === 0 || !!passwordProblem(password) || !!lockedKit} onPress={buildLocked} />
          {!lockedKit && !building ? <Caption>{t('account.protect.makeFirst')}</Caption> : null}
        </>
      ) : null}

      <Button label={t('account.save.openPdf')} icon="export" disabled={!kit} onPress={() => kit && setStatus(openPdf(kit) ? null : t('account.save.blocked'))} />
      {canSaveToFolder ? (
        <>
          <Button label={t('account.save.saveFolder')} icon="import" variant="secondary" disabled={!kit} onPress={() => kit && attempt(async () => ((await saveToFolder(kit)) ? t('account.save.savedTo', { folder: kit.folder }) : null))} />
          <Caption>{t('account.save.folderHint')}</Caption>
        </>
      ) : null}
      {kit && canShare(kit) ? <Button label={t('account.save.share')} icon="export" variant="secondary" onPress={() => attempt(async () => ((await shareFiles(kit)) ? t('account.save.shared') : null))} /> : null}
      <Button label={t('account.save.zip')} icon="import" variant="secondary" disabled={!kit} onPress={() => kit && downloadZip(kit)} />
      {status ? <Caption color={p.tint}>{status}</Caption> : null}
      <ToggleRow label={t('account.save.confirm')} value={confirmed} onChange={setConfirmed} />
      <Button label={t('account.save.done')} disabled={!confirmed} onPress={onDone} />
    </Section>
  );
}
